import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { NodeViewWrapper, ReactNodeViewProps } from '@tiptap/react';
import { getPlaybackUrl } from '../../../services/gnMediaService';
import { useGnContext } from '../GnContext';

/**
 * A user-recorded audio/video clip on the page (spec 046 FR-004). Content is referenced
 * by asset id; playback fetches a short-lived presigned R2 URL (owner-only, FR-027).
 * Native <audio>/<video> controls cover play/pause/stop/volume; video adds fullscreen.
 */
const MediaClipNode: React.FC<ReactNodeViewProps> = ({ node }) => {
  const { assetId, mediaType } = node.attrs as { assetId: number; mediaType: 'AUDIO' | 'VIDEO' };
  const { role } = useGnContext();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!assetId) return;
    getPlaybackUrl(role, assetId)
      .then(u => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [role, assetId]);

  return (
    <NodeViewWrapper as="div">
      <Box sx={{ my: 1.5 }} data-testid="gn-media-clip">
        {error && <Alert severity="warning">This recording could not be loaded.</Alert>}
        {!error && !url && <CircularProgress size={20} />}
        {url && mediaType === 'AUDIO' && (
          <audio controls src={url} style={{ width: '100%', maxWidth: 560 }} />
        )}
        {url && mediaType === 'VIDEO' && (
          <video controls src={url} style={{ width: '100%', maxWidth: 560, borderRadius: 4 }} />
        )}
      </Box>
    </NodeViewWrapper>
  );
};

export default MediaClipNode;
